import {
  disabledReasons,
  enabledReasons,
  optOutReasons,
} from "../../components/Candidate/constants";
import {
  CaptureStatusEnum,
  ScheduledInterviewListItemV2,
  useScheduledInterviewsLazyQuery,
} from "../../graphql";

interface LoadScheduledInterviewsParams {
  fromTime?: string;
  append?: boolean;
  includeFilterOptions?: boolean;
  dateRange: {
    start?: Date;
    end?: Date;
  };
  limit?: number;
  debouncedSearch: string;
  captureStatus?: { value: string } | null;
  recordingReason?: { value: string } | null;
  selectedRecordingStatus?: { value: string } | null;
  selectedPosition?: { value: string } | null;
  selectedDepartment?: { value: string } | null;
  nextPageStartId?: string;
  nextPageStartTimestamp?: string;
  getScheduledInterviews: ReturnType<typeof useScheduledInterviewsLazyQuery>[0];
  onCompleted: (data: any) => void;
}

export const loadScheduledInterviews = ({
  fromTime,
  append = false,
  includeFilterOptions,
  dateRange,
  limit = 50,
  debouncedSearch,
  captureStatus,
  recordingReason,
  selectedRecordingStatus,
  selectedPosition,
  selectedDepartment,
  nextPageStartId,
  nextPageStartTimestamp,
  getScheduledInterviews,
  onCompleted,
}: LoadScheduledInterviewsParams): void => {
  const oneDayInMs = 24 * 60 * 60 * 1000;

  const toTime = dateRange.end
    ? new Date(dateRange.end.getTime() + oneDayInMs).toISOString()
    : undefined;
  const variables: any = {
    fromTime,
    toTime,
    limit,
    query: debouncedSearch,
    includeFilterOptions: includeFilterOptions ?? !append,
  };
  if (captureStatus?.value) {
    variables.captureStatus = captureStatus.value;
  }
  if (recordingReason?.value) {
    variables.importDecisionReason = recordingReason.value;
  }
  if (
    selectedRecordingStatus?.value !== undefined &&
    selectedRecordingStatus.value !== ""
  ) {
    variables.importDecision = selectedRecordingStatus.value === "true";
  }
  if (selectedPosition?.value) {
    variables.positionId = selectedPosition.value;
  }
  if (selectedDepartment?.value) {
    variables.departmentId = selectedDepartment.value;
  }
  if (append && nextPageStartId && nextPageStartTimestamp) {
    variables.pagination = {
      after: {
        id: nextPageStartId,
        timestamp: nextPageStartTimestamp,
      },
    };
  }
  getScheduledInterviews({
    variables,
    fetchPolicy: "cache-and-network",
    onCompleted,
  });
};

export const captureStatusOptions: SelectedOption[] = [
  { label: "Recorded", value: "CAPTURED" },
  { label: "Not recorded", value: "NOT_CAPTURED" },
];

export const recordingReasonOptions = [
  {
    label: "Recording on",
    options: Object.entries(enabledReasons).map(([value, reason]) => ({
      label: reason.shortDescription,
      value,
    })),
  },
  {
    label: "Recording off",
    options: Object.entries(disabledReasons).map(([value, reason]) => ({
      label: reason.shortDescription,
      value,
    })),
  },
  {
    label: "Recording blocked",
    options: Object.entries(optOutReasons).map(([value, reason]) => ({
      label: reason.shortDescription,
      value,
    })),
  },
];

export const recordingStatusOptions: SelectedOption[] = [
  { label: "Recording enabled", value: "true" },
  { label: "Recording disabled", value: "false" },
];

// Helper functions to create filtered options based on backend filter values
export const createFilteredCaptureStatusOptions = (
  availableCaptureStatuses?: string[]
): SelectedOption[] => {
  if (!availableCaptureStatuses) return captureStatusOptions;
  return captureStatusOptions.filter((option) =>
    availableCaptureStatuses.includes(option.value)
  );
};

export const createFilteredRecordingStatusOptions = (
  availableImportDecisions?: boolean[]
): SelectedOption[] => {
  if (!availableImportDecisions) return recordingStatusOptions;
  return recordingStatusOptions.filter((option) => {
    const boolValue = option.value === "true";
    return availableImportDecisions.includes(boolValue);
  });
};

export const createFilteredRecordingReasonOptions = (
  availableReasons?: string[]
): typeof recordingReasonOptions => {
  if (!availableReasons) return recordingReasonOptions;

  return recordingReasonOptions
    .map((group) => ({
      ...group,
      options: group.options.filter((option) =>
        availableReasons.includes(option.value)
      ),
    }))
    .filter((group) => group.options.length > 0); // Remove empty groups
};

export const createFilteredPositionOptions = (
  availablePositions?: Array<{ id: string; value: string }>
): SelectedOption[] => {
  if (!availablePositions) return [];
  return availablePositions.map((position) => ({
    label: position.value,
    value: position.id,
  }));
};

export const createFilteredDepartmentOptions = (
  availableClients?: Array<{ id: string; value: string }>
): SelectedOption[] => {
  if (!availableClients) return [];
  return availableClients.map((client) => ({
    label: client.value,
    value: client.id,
  }));
};

export const getScheduledInterviewsAsTSV = (
  interviews: ScheduledInterviewListItemV2[]
): string => {
  const headers = [
    "Scheduled Start Date and Time",
    "Interview Name",
    "Position Display Title",
    "Candidate Full Name",
    "Interviewer(s) Full Name",
    "Video Conference URL",
    "Recording Status",
    "Recording Reason",
    "Recording Reason Description",
    "Recording Outcome",
  ].join("\t");

  const rows = interviews.map((interview) => {
    const recordingStatus = interview.importDecision
      ? "Recording enabled"
      : "Recording disabled";

    // Get the reason description from constants
    const reasonKey = interview.importDecisionReason || "";
    const reasonObj = enabledReasons[reasonKey] ||
      disabledReasons[reasonKey] ||
      optOutReasons[reasonKey] || {
        shortDescription: "Unknown",
        longDescription: "Unknown reason",
      };

    // Combine interviewer names
    const interviewerNames = (interview.interviewers || [])
      .map((i) => `${i.firstName ?? ""} ${i.lastName ?? ""}`)
      .join(", ");

    // Determine recording outcome based on captureStatus
    const recordingOutcome =
      interview.captureStatus === CaptureStatusEnum.Captured
        ? "Recorded"
        : interview.captureStatus === CaptureStatusEnum.NotCaptured
        ? "Not recorded"
        : "";

    const fields = [
      interview.scheduledStart || "",
      interview.name || "",
      interview.positionTitle || "",
      interview.candidateFullName || "",
      interviewerNames,
      interview.meetingUrl || "",
      recordingStatus,
      reasonObj.shortDescription,
      reasonObj.longDescription,
      recordingOutcome,
    ].join("\t");

    return fields;
  });

  // Combine headers and rows
  return [headers, ...rows].join("\n");
};

export interface SelectedOption {
  label: string;
  value: string;
}

export const findOptionInGroupedOptions = (
  groupedOptions: ReadonlyArray<{
    label: string;
    options: ReadonlyArray<SelectedOption>;
  }>,
  value: string | null | undefined
): SelectedOption | undefined => {
  if (!value) return undefined;
  const allOptions = groupedOptions.flatMap((o) => o.options);
  const option = allOptions.find((opt) => opt.value === value);
  return option || undefined;
};
